<script setup lang="ts">
import type { Tenant } from '~/types/models';
import {
    Bell,
    CheckCircle2,
    CircleAlert,
    ClipboardList,
    Files,
    FileText,
    FolderKanban,
    LayoutDashboard,
    LogOut,
    MessageSquare,
    MoonStar,
    Receipt,
    ShieldCheck,
    SquareKanban,
    SunMedium,
    UserCircle2,
} from 'lucide-vue-next';
import { applyWorkspaceBrandTheme, resolveClientFacingLogoUrl, resolvePortalBrandingContext } from '~/lib/brandTheme'
import { resolveClientPortalContact } from '~/lib/clientPortalIdentity'
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions'
import MobileNavLauncher from '@/components/navigation/MobileNavLauncher.vue'
import WorkspaceCommandMenu from '@/components/navigation/WorkspaceCommandMenu.vue'
import NotificationDrawer from '@/components/notifications/NotificationDrawer.vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarTrigger,
} from '@/components/ui/sidebar';

const authStore = useAuthStore();
const portalStore = useClientPortalStore();
const tenantStore = useTenantStore();
const pageShellPresentation = usePageShellPresentation();
const approvalsStore = useClientPortalApprovalsStore();
const messagesStore = useClientPortalMessagesStore();
const dataStore = useClientPortalDataStore();
const portalFormsStore = useClientPortalFormsStore();
const notificationStore = useNotificationStore();
const route = useRoute();
const { isDark, toggleMode } = useThemeMode();
const isMobileNavOpen = ref(false);
const isNotificationsOpen = ref(false);
const pageHeaderActions = usePageHeaderActions();
const publicWorkspaceBranding = useState<Partial<Tenant> | null>('public-workspace-branding', () => null);
const {
    previewClientId,
    previewQuerySuffix,
    isPreviewMode,
} = usePortalPreviewMode();

const portalClient = computed(() => portalStore.currentClient);
const workspace = computed(() => tenantStore.currentTenant);
const clientSlug = computed(() => String(route.params.tenantSlug || portalClient.value?.slug || '').trim().toLowerCase());
const tenantId = computed(() => String(portalStore.currentTenantId || ''));
const currentAccess = computed(() => portalStore.currentAccess);
const canViewProjects = computed(() => hasClientPortalPermission(currentAccess.value, 'viewProjects'));
const canViewTasks = computed(() => hasClientPortalPermission(currentAccess.value, 'viewTasks'));
const canViewFiles = computed(() => hasClientPortalPermission(currentAccess.value, 'viewFiles'));
const canViewApprovals = computed(() => hasClientPortalPermission(currentAccess.value, 'viewApprovals'));
const canViewMessages = computed(() => hasClientPortalPermission(currentAccess.value, 'viewMessages'));
const canViewInvoices = computed(() => hasClientPortalPermission(currentAccess.value, 'viewInvoices'));
const canViewQuotes = computed(() => hasClientPortalPermission(currentAccess.value, 'viewQuotes'));
const pendingApprovalsCount = computed(() => approvalsStore.approvals.filter(item => item.status === 'pending').length);
const pendingTaskActionsCount = computed(() => dataStore.pendingClientActionCount);
const pendingPortalFormsCount = computed(() => portalFormsStore.pendingCount);
const hasPortalForms = computed(() => portalFormsStore.totalCount > 0 || route.path.includes('/forms'));
const workspaceLogoUrl = computed(() => resolveClientFacingLogoUrl(workspace.value));
const workspaceName = computed(() => workspace.value?.name || portalClient.value?.name || 'Client Portal');
const workspaceMeta = computed(() => (
    isPreviewMode.value
        ? 'Previewing portal experience'
        : (portalClient.value?.name || authStore.user?.email || 'Client access')
));
const previewSuffix = previewQuerySuffix;
const portalBasePath = computed(() => `/portal/${clientSlug.value}`);
const accountPath = computed(() => `${portalBasePath.value}/account${previewSuffix.value}`);

function toMillis(value: unknown): number {
    if (!value) return 0;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds: number }).seconds) * 1000;
    }
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    const parsed = new Date(String(value)).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

const unreadMessagesCount = computed(() => {
    const clientId = portalClient.value?.id;
    const userId = authStore.user?.uid;
    if (!clientId || !userId) return 0;

    const lastSeenKey = `portal:lastSeenMessages:${userId}:${tenantId.value}:${clientId}`;
    const lastSeen = Number(localStorage.getItem(lastSeenKey) || '0');

    return messagesStore.messages.filter(message => (
        message.direction === 'tenant_to_client' && toMillis(message.createdAt) > lastSeen
    )).length;
});
const clientUnreadNotifications = computed(() => notificationStore.unreadCountForContext({
    surface: 'client',
    tenantId: tenantId.value,
    clientId: portalClient.value?.id,
}));

const navItems = computed(() => {
    const base = portalBasePath.value;
    const previewQuery = previewSuffix.value;
    return [
        { label: 'Overview', to: `${base}${previewQuery}`, icon: LayoutDashboard, group: 'Portal' },
        { label: 'Actions', to: `${base}/actions${previewQuery}`, icon: CircleAlert, group: 'Portal' },
        ...(canViewProjects.value ? [{ label: 'Projects', to: `${base}/projects${previewQuery}`, icon: FolderKanban, group: 'Work' }] : []),
        ...(canViewTasks.value ? [{ label: 'Tasks', to: `${base}/tasks${previewQuery}`, badge: pendingTaskActionsCount.value, icon: SquareKanban, group: 'Work' }] : []),
        ...(hasPortalForms.value ? [{ label: 'Forms', to: `${base}/forms${previewQuery}`, badge: pendingPortalFormsCount.value, icon: ClipboardList, group: 'Work' }] : []),
        ...(canViewApprovals.value ? [{ label: 'Approvals', to: `${base}/approvals${previewQuery}`, badge: pendingApprovalsCount.value, icon: CheckCircle2, group: 'Work' }] : []),
        ...(canViewMessages.value ? [{ label: 'Messages', to: `${base}/messages${previewQuery}`, badge: unreadMessagesCount.value, icon: MessageSquare, group: 'Communication' }] : []),
        ...(canViewFiles.value ? [{ label: 'Files', to: `${base}/files${previewQuery}`, icon: Files, group: 'Communication' }] : []),
        ...(canViewInvoices.value ? [{ label: 'Invoices', to: `${base}/invoices${previewQuery}`, icon: Receipt, group: 'Finance' }] : []),
        { label: 'Contracts', to: `${base}/contracts${previewQuery}`, icon: ShieldCheck, group: 'Finance' },
        ...(canViewQuotes.value ? [{ label: 'Quotes', to: `${base}/quotes${previewQuery}`, icon: FileText, group: 'Finance' }] : []),
        ...(canViewQuotes.value ? [{ label: 'Proposals', to: `${base}/proposals${previewQuery}`, icon: FileText, group: 'Finance' }] : []),
        { label: 'Notifications', to: `${base}/notifications${previewQuery}`, icon: Bell, group: 'Account' },
        { label: 'Account', to: `${base}/account${previewQuery}`, icon: UserCircle2, group: 'Account', footer: true },
    ];
});
const primaryNavItems = computed(() => navItems.value.filter((item) => !item.footer));
const portalGroupOrder = ['Portal', 'Work', 'Communication', 'Finance', 'Account'];
const desktopNavGroups = computed(() => portalGroupOrder
    .map(label => ({ label, items: primaryNavItems.value.filter(item => item.group === label) }))
    .filter(group => group.items.length > 0));
const navGroups = computed(() => portalGroupOrder
    .map(label => ({
        id: label.toLowerCase().replace(/\s+/g, '-'),
        label,
        items: navItems.value
            .filter(item => item.group === label)
            .map(item => ({
                id: item.to,
                title: item.label,
                url: item.to,
                icon: item.icon,
                badge: item.badge ? String(item.badge) : undefined,
            })),
    }))
    .filter(group => group.items.length > 0));
const mobileQuickActions = computed(() => pageHeaderActions.value.map(item => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    action: async () => {
        await item.action();
    },
})));
const activePortalItem = computed(() => navItems.value.find(item => isActiveNav(item.to)) || navItems.value[0] || null);
const headerTitle = computed(() => activePortalItem.value?.label || workspaceName.value);

function isActiveNav(to: string): boolean {
    const targetPath = to.split('?')[0] || to;
    if (route.path === targetPath) {
        return true;
    }

    if (targetPath === portalBasePath.value) {
        return false;
    }

    return route.path.startsWith(`${targetPath}/`);
}

function isPortalNavItemActive(_groupLabel: string, url: string): boolean {
    return isActiveNav(url);
}

const accountContact = computed(() => resolveClientPortalContact(
    portalClient.value,
    currentAccess.value,
    authStore.user?.email || portalStore.currentUserEmail,
));
const accountDisplayName = computed(() => (
    accountContact.value?.name
    || authStore.user?.displayName
    || portalClient.value?.contactName
    || portalClient.value?.name
    || 'Portal account'
));
const accountMeta = computed(() => accountContact.value?.email || authStore.user?.email || '');
const accountAvatarUrl = computed(() => (
    accountContact.value?.avatarURL
    || authStore.user?.photoURL
    || portalClient.value?.avatarURL
    || ''
));
const accountInitials = computed(() => {
    const source = accountDisplayName.value || accountMeta.value || 'CP';
    return source
        .split(/[ @.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part: string) => part[0]?.toUpperCase() || '')
        .join('') || 'CP';
});

const initials = computed(() => {
    const name = portalClient.value?.name || workspace.value?.name || authStore.user?.displayName || 'Client';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part: string) => part[0]?.toUpperCase() || '')
        .join('');
});

async function ensureTenantTheme() {
    if (!tenantId.value) {
        applyWorkspaceBrandTheme((publicWorkspaceBranding.value || null) as Tenant | null);
        return;
    }

    const cachedBranding = publicWorkspaceBranding.value;
    if (cachedBranding?.id === tenantId.value) {
        applyWorkspaceBrandTheme(cachedBranding as Tenant);
    }

    try {
        const response = await $fetch<{ tenant: Partial<Tenant> | null }>('/api/public/tenant-branding', {
            query: {
                tenantId: tenantId.value,
            },
        });

        if (response?.tenant) {
            publicWorkspaceBranding.value = {
                id: tenantId.value,
                ...response.tenant,
            };
            applyWorkspaceBrandTheme(publicWorkspaceBranding.value as Tenant);
        }
    } catch (error) {
        console.warn('Unable to load public client portal branding:', error);
    }

    if (tenantStore.currentTenantId !== tenantId.value || !tenantStore.currentTenant) {
        await tenantStore.fetchTenant(tenantId.value);
    }

    applyWorkspaceBrandTheme(resolvePortalBrandingContext(
        tenantId.value,
        publicWorkspaceBranding.value,
        tenantStore.currentTenant,
    ) as Tenant | null);
}

async function handleLogout() {
    const params = new URLSearchParams();
    const redirectTarget = route.fullPath || '/portal';
    params.set('redirect', redirectTarget);
    if (tenantId.value) {
        params.set('tenantId', tenantId.value);
    }

    await authStore.logout();
    portalStore.clearAccess();
    await navigateTo(`/auth/client-login?${params.toString()}`);
}

async function exitPreviewMode() {
    const clientSlug = portalClient.value?.slug;
    if (tenantId.value && clientSlug) {
        await navigateTo(`/${tenantId.value}/clients/${clientSlug}`);
        return;
    }

    await navigateTo('/');
}

async function refreshPortalBadges() {
    const clientId = portalClient.value?.id;
    if (!tenantId.value || !clientId) return;

    await Promise.all([
        dataStore.initialize(tenantId.value, clientId),
        portalFormsStore.fetchForClient(tenantId.value, clientId),
        canViewApprovals.value ? approvalsStore.fetchForClient(tenantId.value, clientId) : Promise.resolve(),
        canViewMessages.value ? messagesStore.fetchThread(tenantId.value, clientId) : Promise.resolve(),
    ]);
}

function markMessagesSeen() {
    const clientId = portalClient.value?.id;
    const userId = authStore.user?.uid;
    if (!clientId || !userId) return;
    const key = `portal:lastSeenMessages:${userId}:${tenantId.value}:${clientId}`;
    localStorage.setItem(key, String(Date.now()));
}

await ensureTenantTheme();

onMounted(async () => {
    await refreshPortalBadges();
    notificationStore.startRealtime();

    if (route.path.includes('/messages')) {
        markMessagesSeen();
    }
});

watch(() => [tenantId.value, portalClient.value?.id], async () => {
    await ensureTenantTheme();
    await refreshPortalBadges();
});

watch(() => route.path, async (path) => {
    if (path.includes('/messages')) {
        markMessagesSeen();
    }
    await refreshPortalBadges();
});
</script>

<template>
    <SidebarProvider class="bg-background">
        <NotificationDrawer
            v-model:open="isNotificationsOpen"
            surface="client"
            :tenant-id="tenantId"
            :client-id="portalClient?.id"
        />
        <Sidebar
            variant="floating"
            class="app-shell-sidebar border-r-0 bg-background [--sidebar:var(--card)] [--sidebar-accent-foreground:var(--foreground)] [--sidebar-border:var(--border)]"
        >
            <SidebarHeader class="px-3 pb-2 pt-4">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            size="lg"
                            class="hover:bg-muted/45 active:bg-muted/55 data-[active=true]:bg-muted/70 data-[state=open]:hover:bg-muted/45"
                        >
                            <div class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl border border-sidebar-border/80 bg-sidebar-primary/10 text-xs font-semibold text-sidebar-primary">
                                <img
                                    v-if="workspaceLogoUrl"
                                    :src="workspaceLogoUrl"
                                    :alt="`${workspace?.name || 'Workspace'} logo`"
                                    class="h-full w-full object-cover"
                                >
                                <span v-else>{{ initials }}</span>
                            </div>
                            <div class="grid flex-1 text-left text-sm leading-tight">
                                <span class="truncate font-semibold">{{ workspace?.name || portalClient?.name || 'Client Portal' }}</span>
                                <span class="truncate text-xs text-muted-foreground">
                                    {{ isPreviewMode ? 'Previewing portal experience' : (portalClient?.name || authStore.user?.email) }}
                                </span>
                            </div>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent class="no-scrollbar px-3 pb-3 pt-1">
                <SidebarGroup
                    v-for="group in desktopNavGroups"
                    :key="group.label"
                    :class="group.label !== 'Portal' ? 'pb-2 pt-4 first:pt-1' : 'pb-3 pt-1'"
                >
                    <p v-if="group.label !== 'Portal'" class="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-sidebar-foreground/45">
                        {{ group.label }}
                    </p>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem v-for="item in group.items" :key="item.to">
                                <SidebarMenuButton
                                    as-child
                                    :is-active="isActiveNav(item.to)"
                                    class="hover:bg-muted/45 active:bg-muted/55 data-[active=true]:bg-muted/70 data-[state=open]:hover:bg-muted/45"
                                >
                                    <NuxtLink :to="item.to">
                                        <component :is="item.icon" class="size-4" />
                                        <span>{{ item.label }}</span>
                                    </NuxtLink>
                                </SidebarMenuButton>
                                <SidebarMenuBadge v-if="item.badge && item.badge > 0">{{ item.badge }}</SidebarMenuBadge>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter class="space-y-2 border-t border-sidebar-border/60 px-3 pb-4 pt-3">
                <NuxtLink
                    :to="accountPath"
                    class="group flex items-center gap-3 rounded-2xl border border-sidebar-border/70 bg-muted/30 px-3 py-2.5 transition hover:bg-muted/45"
                    :class="{ 'border-sidebar-primary/45 bg-muted/70': isActiveNav(accountPath) }"
                >
                    <Avatar class="h-9 w-9 rounded-xl border border-sidebar-border/80">
                        <AvatarImage :src="accountAvatarUrl || undefined" :alt="accountDisplayName" />
                        <AvatarFallback class="rounded-xl bg-sidebar-primary/15 text-xs font-semibold text-sidebar-primary">
                            {{ accountInitials }}
                        </AvatarFallback>
                    </Avatar>
                    <div class="min-w-0 flex-1">
                        <p class="truncate text-xs font-medium uppercase tracking-[0.14em] text-sidebar-foreground/65">
                            Account
                        </p>
                        <p class="truncate text-sm font-medium text-sidebar-foreground">
                            {{ accountDisplayName }}
                        </p>
                        <p class="truncate text-xs text-sidebar-foreground/60">
                            {{ accountMeta }}
                        </p>
                    </div>
                </NuxtLink>
                <Button variant="ghost" size="sm" class="w-full justify-start gap-2 text-muted-foreground hover:text-foreground" @click="handleLogout">
                    <LogOut class="size-4" />
                    Log out
                </Button>
            </SidebarFooter>
        </Sidebar>

        <SidebarInset class="min-w-0 bg-background" :class="{ 'app-shell-immersive': pageShellPresentation === 'immersive' }">
            <Card
                class="app-shell-header flex-row"
                :class="{ 'app-shell-header-immersive': pageShellPresentation === 'immersive' }"
                role="banner"
            >
                <div class="flex min-w-0 flex-1 items-center gap-3">
                    <div class="hidden md:block">
                        <SidebarTrigger />
                    </div>
                    <p class="min-w-0 truncate text-base font-semibold tracking-tight text-foreground">{{ headerTitle }}</p>
                </div>

                <div class="flex flex-1 justify-center px-1 md:px-4">
                    <WorkspaceCommandMenu
                        :workspace-name="workspaceName"
                        :nav-groups="navGroups"
                        :account-items="[]"
                        :quick-actions="[]"
                        :is-item-active="isPortalNavItemActive"
                    />
                </div>

                <div class="ml-auto flex flex-1 items-center justify-end gap-2">
                    <Button
                        v-for="item in pageHeaderActions"
                        :key="item.id"
                        :variant="item.variant || 'default'"
                        class="hidden md:inline-flex"
                        @click="item.action"
                    >
                        <component :is="item.icon" v-if="item.icon" class="mr-2 size-4" />
                        {{ item.label }}
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        class="relative"
                        @click="isNotificationsOpen = true"
                    >
                        <Bell class="size-4" />
                        <span
                            v-if="clientUnreadNotifications > 0"
                            class="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground"
                        >
                            {{ clientUnreadNotifications > 99 ? '99+' : clientUnreadNotifications }}
                        </span>
                        <span class="sr-only">Open notifications</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        @click="toggleMode"
                    >
                        <SunMedium v-if="isDark" class="size-4" />
                        <MoonStar v-else class="size-4" />
                        <span class="sr-only">Toggle dark mode</span>
                    </Button>
                </div>
            </Card>

            <main
                class="app-shell-main relative"
                :class="pageShellPresentation === 'immersive' ? 'app-shell-main-immersive' : 'bg-background'"
            >
                <div
                    v-if="isPreviewMode"
                    class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-400/45 bg-amber-50/88 px-4 py-3 text-sm backdrop-blur-md dark:bg-amber-950/40"
                    :class="pageShellPresentation === 'immersive'
                        ? 'absolute inset-x-4 top-16 z-20 sm:inset-x-5 lg:inset-x-6'
                        : 'mb-4'"
                >
                    <p class="text-amber-950 dark:text-amber-100">
                        Preview mode active: viewing portal as
                        <span class="font-semibold">{{ portalClient?.name || 'selected client' }}</span>.
                    </p>
                    <Button size="sm" variant="outline" class="border-amber-400/45 text-amber-950 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-950/40" @click="exitPreviewMode">
                        Exit preview
                    </Button>
                </div>

                <slot />
            </main>

            <MobileNavLauncher
                v-model="isMobileNavOpen"
                :workspace-name="workspaceName"
                :workspace-meta="workspaceMeta"
                :nav-groups="navGroups"
                :quick-actions="mobileQuickActions"
                :is-item-active="isPortalNavItemActive"
            />
        </SidebarInset>
    </SidebarProvider>
</template>
